/* eslint-disable react-hooks/exhaustive-deps */
import {
  marketingOverviewFilter,
  ICampaignResponse,
} from '@dataTypes/marketing.type'
import { useAppSelector } from '@store/hooks'
import { isDateInRange } from '@utils/date.utils'
import { format } from 'date-fns'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

interface IInOutMessage {
  x: string
  Inbound: number
  Outbound: number
}

interface InOutMessageHooks {
  campaignDropdownOptions: string[]
  campaigns: ICampaignResponse[]
  data: IInOutMessage[]
  selectedCampaign: string
  setSelectedCampaign: Dispatch<SetStateAction<string>>
}

const useInOutMessage = (
  overviewDateFilter: marketingOverviewFilter,
): InOutMessageHooks => {
  const campaigns = useAppSelector((state) => state.marketing.campaigns)
  const selectedDate = useAppSelector((state) => state.marketing.selectedDate)
  const [selectedCampaign, setSelectedCampaign] = useState<string>('Select all')
  const [data, setData] = useState<IInOutMessage[]>([])

  const campaignDropdownOptions = useMemo(() => {
    const response: string[] = ['Select all']
    response.push(...campaigns.map((c) => c.name))
    return response
  }, [campaigns])

  const generateXDates = useCallback(
    (filter: marketingOverviewFilter): { type: string; value: Date[] } => {
      const _xValues: Date[] = []
      let type: string = ''
      const today = new Date(selectedDate)
      switch (filter) {
        case marketingOverviewFilter['1D']:
          for (let i = 0; i < 24; i += 4) {
            const _value = new Date(selectedDate)
            _value.setHours(i, 0, 0, 0)
            _xValues.push(_value)
          }
          const _value = new Date(selectedDate)
          _value.setHours(23, 59, 59, 999)
          _xValues.push(_value)
          const _next = new Date(selectedDate)
          _next.setDate(_next.getDate() + 1)
          _next.setHours(0, 0, 0, 0)
          _xValues.push(_next)
          type = 'time'
          break
        case marketingOverviewFilter['1W']:
          for (let i = 6; i >= -1; i--) {
            const date = new Date(today)
            date.setDate(today.getDate() - i)
            _xValues.push(date)
          }
          type = 'date'
          break
        case marketingOverviewFilter['1M']:
          const oneMonthAgo = new Date(today)
          oneMonthAgo.setMonth(today.getMonth() - 1)
          const interval = 30 / 9 // 9 intervals for 10 points

          for (let i = 0; i < 11; i++) {
            const date = new Date(oneMonthAgo)
            date.setDate(oneMonthAgo.getDate() + Math.round(i * interval))
            _xValues.push(date)
          }
          type = 'date'
          break
        case marketingOverviewFilter['1Y']:
          for (let i = 11; i >= -1; i--) {
            const date = new Date(today)
            date.setMonth(today.getMonth() - i)
            date.setDate(1)
            _xValues.push(date)
          }
          type = 'month'
          break
        case marketingOverviewFilter['YTD']:
          for (let i = 0; i < 13; i++) {
            const date = new Date(today)
            date.setMonth(i)
            date.setDate(1)
            _xValues.push(date)
          }
          type = 'month'
          break
      }
      return { type, value: _xValues }
    },
    [selectedDate],
  )

  const getXValue = (x: Date, type: string): string => {
    switch (type) {
      case 'time':
        return format(x, 'HH:mm')
      case 'date':
        return format(x, 'MMM d')
      case 'month':
        return format(x, 'MMM yyyy')
      default:
        return ''
    }
  }

  const calculateInOutMessage = useCallback((): void => {
    const _campaigns =
      selectedCampaign === 'Select all'
        ? campaigns
        : campaigns.filter((c) => c.name === selectedCampaign)

    const xValues = generateXDates(overviewDateFilter)
    const _data: IInOutMessage[] = []
    xValues.value.forEach((x, index) => {
      if (index !== xValues.value.length - 1) {
        const dateRange = { start: x, end: xValues.value[index + 1] }

        const inboundCount = _campaigns.reduce((acc, campaign) => {
          return (
            acc +
            campaign.campaign_message.filter(
              (m) =>
                m.message_type === 'Incoming' &&
                m.date_time_sent &&
                isDateInRange(
                  m.date_time_sent,
                  dateRange.start,
                  dateRange.end,
                  false,
                ),
            ).length
          )
        }, 0)
        const outboundCount = _campaigns.reduce((acc, campaign) => {
          return (
            acc +
            campaign.campaign_message.filter(
              (m) =>
                m.message_type === 'Outgoing' &&
                m.date_time_sent &&
                isDateInRange(
                  m.date_time_sent,
                  dateRange.start,
                  dateRange.end,
                  false,
                ),
            ).length
          )
        }, 0)

        _data.push({
          x: getXValue(x, xValues.type),
          Inbound: inboundCount,
          Outbound: outboundCount,
        })
        setData(_data)
      }
    })
  }, [overviewDateFilter, selectedCampaign, selectedDate, campaigns])

  useEffect(() => {
    calculateInOutMessage()
  }, [overviewDateFilter, selectedCampaign, selectedDate, campaigns])

  return {
    campaignDropdownOptions,
    campaigns,
    data,
    selectedCampaign,
    setSelectedCampaign,
  }
}

export default useInOutMessage
